import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import logo from '@/assets/svg/logo.svg';
import { keycloakLogout } from '@/config/keycloak';
import useUserProfile from '@/hooks/useKeycloakUser';
import PortalNavigation from '@/components/PortalNavigation/PortalNavigation';
import { GroupsEnum } from '@/types/users';
import menuIcon from '@/assets/svg/menu.svg';
import closeIcon from '@/assets/svg/close.svg';
import { hasAnyGroup, normalizeGroup } from '@/utils/groups';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { getPageTitle } from '@/config/routes';
import { getUserDisplayName, getUserLogin } from '@/utils/user';

const Layout = ({ children }: { children?: ReactNode }) => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { keycloak, user } = useUserProfile();
  const isClientHome = location.pathname === '/' && hasAnyGroup(user?.groups, [GroupsEnum.CLIENT]);
  const pageTitle = getPageTitle(location.pathname, isClientHome);
  const canLogout =
    hasAnyGroup(user?.groups, [GroupsEnum.ADMIN]) ||
    (user?.groups ?? []).some((group) => normalizeGroup(group).startsWith('/rd'));

  useEffect(() => {
    if (!userMenuOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!userMenuRef.current?.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setUserMenuOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [userMenuOpen]);

  const displayName = getUserDisplayName(user);
  const login = getUserLogin(user);
  const initials = displayName
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
  const profile = (
    <>
      <span className="flex min-w-0 max-w-[48vw] flex-col items-end sm:max-w-none">
        <span className="w-full break-words text-right text-xs font-medium leading-tight text-gray-800 md:text-base">
          {displayName}
        </span>
        <span
          className="w-full truncate text-right text-[11px] font-normal text-gray-500 sm:text-xs"
          title={login}
        >
          {login}
        </span>
      </span>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-black text-lg text-white">
        {initials}
      </span>
    </>
  );

  const closeUserMenu = () => {
    setShowConfirm(true);
    setUserMenuOpen(false);
  };

  return (
    <div className="relative flex min-h-screen flex-col bg-gray-50 md:flex-row">
      {menuOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 cursor-default bg-black bg-opacity-30 md:hidden"
          onClick={() => setMenuOpen(false)}
          aria-label="Закрыть меню"
        />
      )}

      <aside
        className={`fixed left-0 top-0 z-50 h-full w-72 overflow-x-hidden border-r border-gray-200 bg-white pt-6 transition-transform duration-200 md:hidden ${menuOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-4 top-4 h-9 w-9"
          onClick={() => setMenuOpen(false)}
          aria-label="Закрыть меню"
        >
          <img src={closeIcon} alt="" className="h-6 w-6" />
        </Button>
        <div className="mx-auto mb-10 flex h-10 w-10 items-center justify-center rounded-lg">
          <img src={logo} alt="Familia" className="h-full w-full object-contain" />
        </div>
        <PortalNavigation onNavigate={() => setMenuOpen(false)} />
        <div className="absolute bottom-4 left-0 w-full text-center text-xs text-gray-400">
          © Familia
        </div>
      </aside>

      <main className="flex min-w-0 flex-1 flex-col overflow-hidden pt-0">
        <header className="flex w-full flex-row items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3 md:px-8">
          <div className="md:hidden">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="justify-start"
              onClick={() => setMenuOpen(true)}
              aria-label="Открыть меню"
            >
              <img src={menuIcon} alt="" className="h-7 w-7" />
            </Button>
          </div>
          <div className="hidden md:block">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setDesktopMenuOpen((open) => !open)}
              aria-label={desktopMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
            >
              {desktopMenuOpen ? (
                <img src={closeIcon} alt="" className="h-7 w-7" />
              ) : (
                <img src={menuIcon} alt="" className="h-7 w-7" />
              )}
            </Button>
          </div>
          <div className="min-w-0 flex-1">
            {pageTitle && (
              <h1 className="truncate text-left text-sm font-medium md:text-2xl">{pageTitle}</h1>
            )}
          </div>
          <div ref={userMenuRef} className="relative min-w-0 shrink">
            {canLogout ? (
              <Button
                type="button"
                variant="ghost"
                size="bare"
                className="min-w-0 gap-2 hover:bg-transparent"
                onClick={() => setUserMenuOpen((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
              >
                {profile}
              </Button>
            ) : (
              <div className="flex min-w-0 items-center gap-2">{profile}</div>
            )}
            {canLogout && userMenuOpen && (
              <div
                role="menu"
                className="absolute right-0 top-full z-50 mt-2 flex min-w-[120px] flex-col rounded-lg border border-gray-200 bg-white py-2 shadow-lg"
              >
                <Button
                  type="button"
                  role="menuitem"
                  variant="ghost"
                  size="sm"
                  fullWidth
                  className="justify-start font-normal"
                  onClick={closeUserMenu}
                >
                  Выход
                </Button>
              </div>
            )}
          </div>
        </header>

        <div className="flex h-auto min-h-0 min-w-0 md:h-[calc(100dvh-64px)]">
          <aside
            className={`hidden h-full shrink-0 overflow-hidden bg-white transition-[width] duration-300 ease-in-out motion-reduce:transition-none md:block ${desktopMenuOpen ? 'w-72 border-r border-gray-200' : 'w-0 border-r-0'}`}
            aria-hidden={!desktopMenuOpen}
          >
            <div className="flex h-full w-72 min-w-72 flex-col pt-6">
              <div className="mx-auto mb-10 flex h-10 w-10 items-center justify-center rounded-lg">
                <img src={logo} alt="Familia" className="h-full w-full object-contain" />
              </div>
              <PortalNavigation />
              <span className="mt-auto w-full pb-4 text-center text-xs text-gray-400">
                © Familia
              </span>
            </div>
          </aside>
          <div className="h-full min-w-0 max-w-full flex-1 overflow-x-hidden overflow-y-auto p-4">
            {children ?? <Outlet />}
          </div>
        </div>
      </main>

      {showConfirm && (
        <Modal
          title="Вы уверены, что хотите выйти?"
          className="max-w-xs"
          onClose={() => setShowConfirm(false)}
          actions={
            <>
              <Button
                type="button"
                variant="neutral"
                size="sm"
                onClick={() => setShowConfirm(false)}
              >
                Нет
              </Button>
              <Button type="button" size="sm" onClick={() => keycloakLogout(keycloak)}>
                Да
              </Button>
            </>
          }
        >
          <p className="text-sm text-gray-600">Текущий сеанс будет завершён.</p>
        </Modal>
      )}
    </div>
  );
};

export default Layout;
